import { useState, useEffect } from 'react'
import { CreditCard, AlertCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { billingService, type BillingStatusResult } from '@/services/billing-service'
import { useAuthStore } from '@/store/auth-store'

const PLAN_LABEL: Record<string, string> = {
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)))
}

export function BillingSettings() {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN'

  const [status, setStatus] = useState<BillingStatusResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpening, setIsOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setIsLoading(true)
      try {
        const s = await billingService.getStatus()
        if (!cancelled) setStatus(s)
      } catch {
        if (!cancelled) setStatus({ stripeConfigured: false })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  async function handleManageBilling() {
    setIsOpening(true)
    setError(null)
    try {
      const url = await billingService.createPortalSession()
      window.location.href = url
    } catch {
      setError('Could not open the billing portal. Please try again.')
      setIsOpening(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Billing</h3>
        <p className="text-sm text-muted-foreground">Only administrators can manage billing.</p>
      </div>
    )
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading billing status…</p>
  }

  if (!status || status.stripeConfigured === false) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Billing</h3>
        <div className="flex items-start gap-2 rounded-md border p-4 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Billing isn’t set up for this account yet. Please contact support to manage your subscription.</span>
        </div>
      </div>
    )
  }

  const trialDays = status.plan === 'trial' ? daysUntil(status.trialEndsAt) : null

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Billing</h3>
        <p className="text-sm text-muted-foreground">
          Manage your payment method, view invoices, or cancel your subscription.
        </p>
      </div>

      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Plan</span>
          <span className="font-medium">{PLAN_LABEL[status.plan] ?? status.plan}</span>
        </div>

        {trialDays !== null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Trial ends</span>
            <span className="font-medium">
              {formatDate(status.trialEndsAt)} ({trialDays} day{trialDays === 1 ? '' : 's'} left)
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Seats</span>
          <span className="font-medium">{status.seatCount}</span>
        </div>

        {status.plan !== 'trial' && status.currentPeriodEnd && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {status.cancelAtPeriodEnd ? 'Subscription ends' : 'Next billing date'}
            </span>
            <span className="font-medium">{formatDate(status.currentPeriodEnd)}</span>
          </div>
        )}

        {status.cancelAtPeriodEnd && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Your subscription is set to cancel on {formatDate(status.currentPeriodEnd)}. You can reactivate it from the billing portal.</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button onClick={handleManageBilling} disabled={isOpening}>
        <CreditCard className="h-4 w-4 mr-2" />
        {isOpening ? 'Opening…' : 'Manage Billing'}
        {!isOpening && <ExternalLink className="h-4 w-4 ml-2" />}
      </Button>
    </div>
  )
}
